import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { FcGoogle } from 'react-icons/fc';
import { signUp, signUpWithGoogle } from '../../Redux/Auth/authActions';
import '../../Stylesheets/Auth/SignUp.css';
import Navbar from '../../Components/Common/Navbar';
import BubbleBackground from '../../Components/Common/BubbleBackground';
import NewsletterSection from '../../Components/Home/FooterWithNewsletter';
import PageWrapper from '../../Components/Common/PageWrapper';
import AlertBox from '../../Components/Common/AlertBox';

const SignUp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.auth);
  const [passwordError, setPasswordError] = useState('');

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
  });

  const [localAlert, setLocalAlert] = useState({ type: 'error', message: '' });

  const setAlert = (message, type = 'error') => setLocalAlert({ type, message });
  const clearAlert = () => setLocalAlert({ type: 'error', message: '' });

  const isPasswordValid = (password) => {
    const regex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    return regex.test(password);
  };

  const handleChange = (e) => {
    if (localAlert.message) clearAlert();
    if (passwordError) setPasswordError('');
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName || !formData.email || !formData.password) {
      setAlert('All fields are required');
      return;
    }

    if (!isPasswordValid(formData.password)) {
      const msg =
        'Password must be 8+ chars, include uppercase, lowercase, number, and special character.';
      setPasswordError(msg);
      setAlert(msg, 'error');
      return;
    }

    setPasswordError('');
    clearAlert();
    dispatch(signUp(formData.fullName, formData.email, formData.password, navigate));
  };

  const handleGoogleSignup = () => {
    clearAlert();
    setPasswordError('');
    dispatch(signUpWithGoogle(navigate));
  };

  useEffect(() => {
    if (error) setAlert(error, 'error');
  }, [error]);

  return (
    <>
    <div className="auth-page-bg">
      <Navbar />
      <BubbleBackground />

      <PageWrapper>
        <div className="auth-wrap">
          <div className="auth-card">
            <div className="auth-head">
              <h2 className="auth-title">Create your account</h2>
              <p className="auth-subtitle">Sign up to manage your site projects smarter.</p>
            </div>

            <AlertBox type={localAlert.type} message={localAlert.message} onClose={clearAlert} />

            <button className="auth-google" onClick={handleGoogleSignup} type="button">
              <FcGoogle size={20} />
              <span>Sign up with Google</span>
            </button>

            <div className="auth-divider">
              <span>Or, sign up with your email</span>
            </div>

            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="auth-field">
                <label className="auth-label">Full Name</label>
                <input
                  type="text"
                  name="fullName"
                  placeholder="Enter your name"
                  className="auth-input"
                  value={formData.fullName}
                  onChange={handleChange}
                  autoComplete="name"
                />
              </div>

              <div className="auth-field">
                <label className="auth-label">Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  className="auth-input"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                />
              </div>

              <div className="auth-field">
                <label className="auth-label">Password</label>
                <input
                  type="password"
                  name="password"
                  placeholder="Create a password"
                  className="auth-input"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
                {passwordError && <p className="auth-help auth-help--error">{passwordError}</p>}
                {!passwordError && (
                  <p className="auth-help">8+ chars, 1 uppercase, 1 number, 1 special character.</p>
                )}
              </div>

              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Account'}
              </button>
            </form>

            <p className="auth-foot">
              Already have an account? <Link to="/signin">Sign in</Link>
            </p>
          </div>
        </div>
      </PageWrapper>

      <NewsletterSection />
      </div>
    </>
  );
};

export default SignUp;
